import { useLayoutEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

// Virtual mouse cursor for the demos. Glides between scripted points
// and pulses on click. Uses pixel-perfect ref-based targeting: each
// clickable element in the demo carries `data-cursor-target="name"`,
// and the timeline says `setCursor({ target: 'name', click })`. The
// cursor reads the target's `getBoundingClientRect()` relative to the
// `containerRef` and positions exactly on its center.
//
// Fallback mode (`{ x, y }` as percentages) is still supported for
// transit positions that don't anchor on a specific element.
//
// Prop shape:
//   cursor = null                              → hidden
//   cursor = { target: 'name', click? }        → anchored to data-target
//   cursor = { x: 50, y: 60, click? }          → fallback percentage
//
// Render as the LAST child of the demo's `position: relative` container
// so the cursor floats above everything (z-index 60).
export default function DemoCursor({ cursor, containerRef }) {
  const [pos, setPos] = useState(null) // { x, y } in pixels relative to container
  const lastPos = useRef(null) // remember last pixel position for smooth transit

  useLayoutEffect(() => {
    if (!cursor) {
      setPos(null)
      lastPos.current = null
      return
    }

    function measure() {
      const container = containerRef?.current
      if (!container) return null

      const containerRect = container.getBoundingClientRect()

      // Target mode: look up the element by data-cursor-target, anchor
      // on its center.
      if (cursor.target) {
        const el = container.querySelector(`[data-cursor-target="${cursor.target}"]`)
        if (el) {
          const rect = el.getBoundingClientRect()
          return {
            x: rect.left - containerRect.left + rect.width / 2,
            y: rect.top - containerRect.top + rect.height / 2,
          }
        }
        // Target missing this frame (e.g., AnimatePresence still entering)
        // — fall back to last known position so we don't snap to (0,0).
        return lastPos.current
      }

      // Percentage fallback for transits between named targets.
      if (typeof cursor.x === 'number' && typeof cursor.y === 'number') {
        return {
          x: (cursor.x / 100) * containerRect.width,
          y: (cursor.y / 100) * containerRect.height,
        }
      }

      return lastPos.current
    }

    const next = measure()
    if (next) {
      setPos(next)
      lastPos.current = next
    }
  }, [cursor, containerRef])

  // Re-measure on container resize so the cursor keeps tracking the
  // right element as the demo card grows / shrinks across breakpoints.
  useLayoutEffect(() => {
    const el = containerRef?.current
    if (!el) return
    const observer = new ResizeObserver(() => {
      if (!cursor) return
      // Repeat the measurement above using the latest layout.
      const containerRect = el.getBoundingClientRect()
      if (cursor.target) {
        const t = el.querySelector(`[data-cursor-target="${cursor.target}"]`)
        if (!t) return
        const rect = t.getBoundingClientRect()
        const next = {
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top + rect.height / 2,
        }
        setPos(next)
        lastPos.current = next
      } else if (typeof cursor.x === 'number' && typeof cursor.y === 'number') {
        const next = {
          x: (cursor.x / 100) * containerRect.width,
          y: (cursor.y / 100) * containerRect.height,
        }
        setPos(next)
        lastPos.current = next
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [cursor, containerRef])

  return (
    <AnimatePresence>
      {cursor && pos && (
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{
            opacity: 1,
            scale: 1,
            top: pos.y,
            left: pos.x,
          }}
          exit={{ opacity: 0, scale: 0.6 }}
          transition={{
            opacity: { duration: 0.2 },
            scale: { duration: 0.2 },
            top: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
            left: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
          }}
          className="pointer-events-none absolute"
          style={{ zIndex: 60 }}
          aria-hidden="true"
        >
          <div className="relative">
            {/* Pointer arrow — tip at SVG (0,0) so the wrapper's top/left
                lands precisely at the target center. */}
            <svg
              width="22"
              height="26"
              viewBox="-1 -1 22 26"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))' }}
            >
              <path
                d="M 0 0 L 0 17 L 5 12.5 L 8.5 20.5 L 11 19.5 L 7.5 11.8 L 14 11.5 Z"
                fill="#ffffff"
                stroke="#0b1229"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            </svg>

            <AnimatePresence>
              {cursor.click && (
                <>
                  <motion.span
                    key="ring1"
                    initial={{ opacity: 0.75, scale: 0.4 }}
                    animate={{ opacity: 0, scale: 1.7 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.55, ease: 'easeOut' }}
                    className="absolute rounded-full"
                    style={{
                      width: 36,
                      height: 36,
                      top: -18,
                      left: -18,
                      border: '2px solid #e9c349',
                    }}
                  />
                  <motion.span
                    key="ring2"
                    initial={{ opacity: 0.5, scale: 0.4 }}
                    animate={{ opacity: 0, scale: 2.4 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.75, ease: 'easeOut', delay: 0.05 }}
                    className="absolute rounded-full"
                    style={{
                      width: 36,
                      height: 36,
                      top: -18,
                      left: -18,
                      border: '1.5px solid rgba(233,195,73,0.7)',
                    }}
                  />
                </>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
